import { match } from '@formatjs/intl-localematcher';
import i18n from 'i18next';
export { i18n };
import { initReactI18next } from 'react-i18next';

import { messages } from '@client/locales';
import { logger } from '@client/utils/logger.util';
import {
  CDefaultLocale,
  ELocales,
  STORE_LOCALE_KEY,
  isRtlLocale,
} from '@common/types';

/**
 * Get the browser's preferred locales.
 * @returns {string[]} - An array of browser locales.
 */
export function getBrowserLocales(): string[] {
  const result: string[] = [];

  if (navigator.languages?.length) {
    const supportedLocales = Object.values(ELocales);

    for (const lang of navigator.languages) {
      if (
        supportedLocales.some((locale) =>
          lang.toLowerCase().startsWith(locale.toLowerCase()),
        )
      ) {
        result.push(lang);
      }
    }
  }

  return Array.isArray(result) && result.length ? result : [CDefaultLocale];
}

/**
 * Get the stored locale from localStorage.
 * @returns {ELocales} - The stored locale, or the default locale if not found.
 */
export function getStoredLocale(): ELocales {
  if (typeof window === 'undefined' || !window.localStorage) {
    return CDefaultLocale;
  }

  return (window.localStorage.getItem(STORE_LOCALE_KEY) ||
    CDefaultLocale) as ELocales;
}

/**
 * Set the stored locale in localStorage.
 * @param {string} locale - The locale to store.
 */
export function setStoredLocale(locale: string): void {
  window.localStorage.setItem(STORE_LOCALE_KEY, locale);
}

/**
 * Get the user's locale based on browser preferences and stored preference.
 * @returns {ELocales} - The user's locale, or the default locale if no match is found.
 */
export function userLocale(): ELocales {
  const browserLocales = getBrowserLocales();
  const storedLocale: ELocales = getStoredLocale();
  if (storedLocale) return storedLocale as ELocales;
  return match(browserLocales, Object.keys(messages), storedLocale) as ELocales;
}

export const applyDocumentDirection = (locale: ELocales): void => {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  html.dir = isRtlLocale(locale) ? 'rtl' : 'ltr';
  html.lang = locale;
};

export const initI18n = (
  lng: ELocales = CDefaultLocale,
  resources: Record<string, { translation: Record<string, string> }>,
) => {
  i18n
    .use(initReactI18next)
    .init({
      lng,
      fallbackLng: CDefaultLocale,
      interpolation: { escapeValue: false },
      resources,
    })
    .then(() => {
      setStoredLocale(lng);
      applyDocumentDirection(lng);
      logger.info('i18n initialized successfully');
    })
    .catch((error) => {
      logger.error('i18n initialization failed', error);
    });

  return i18n;
};

export const getTranslatedMessage = (key: string, entity?: string) => {
  if (i18n.isInitialized) {
    return entity ? i18n.t(key, { entity }) : i18n.t(key);
  } else {
    // Handle cases where i18next might not be fully initialized yet
    logger.warn('i18next not initialized. Returning key:', key);
    return key;
  }
};
